import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Divider,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Fade
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import HandshakeIcon from '@mui/icons-material/Handshake';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';

const methodologySteps = [
  {
    title: "Village Survey & Group Formation",
    icon: GroupsIcon,
    description: "We form Joint Liability Groups (JLGs) of 3-5 members from similar socio-economic backgrounds. These groups create a support system and shared responsibility for loan repayment.",
    color: "#1565C0"
  },
  {
    title: "Financial Assessment",
    icon: AccountBalanceIcon,
    description: "Our team conducts thorough financial assessments of potential borrowers, evaluating their income, business plans, and repayment capacity to ensure responsible lending.",
    color: "#2E7D32"
  },
  {
    title: "Training & Orientation",
    icon: AssignmentTurnedInIcon,
    description: "Members receive comprehensive training on financial literacy, business management, and our lending methodology to ensure they understand their responsibilities.",
    color: "#C62828"
  },
  {
    title: "Loan Disbursement",
    icon: AccountBalanceWalletIcon,
    description: "After successful training and assessment, loans are disbursed to group members with clear terms and flexible repayment schedules tailored to their business cycles.",
    color: "#6A1B9A"
  },
  {
    title: "Regular Monitoring",
    icon: PersonSearchIcon,
    description: "Our field officers conduct regular visits to monitor business progress, provide guidance, and ensure timely repayment through center meetings.",
    color: "#00695C"
  },
  {
    title: "Continuous Support",
    icon: HandshakeIcon,
    description: "We provide ongoing business development support, financial advisory services, and access to larger loans for successfully graduating members.",
    color: "#EF6C00"
  }
];

const Methodology = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Box 
      sx={{ 
        pt: { xs: 8, md: 12 },
        pb: { xs: 8, md: 12 },
        bgcolor: 'background.default',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(25,118,210,0.03) 0%, rgba(25,118,210,0) 70%)',
          top: '0%',
          right: '-20%',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(156,39,176,0.03) 0%, rgba(156,39,176,0) 70%)',
          bottom: '0%',
          left: '-20%',
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header Section */}
        <Fade in={true} timeout={1000}>
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography
              variant="overline"
              sx={{
                color: 'secondary.main',
                fontWeight: 600,
                letterSpacing: 1.5,
                mb: 1,
                display: 'block'
              }}
            >
              OUR APPROACH
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                color: 'text.primary',
                mb: 2
              }}
            >
              Methodology
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 400,
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                color: 'text.secondary',
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              Our systematic approach to microfinance ensures sustainable impact through
              group-based lending, thorough assessment, and continuous support.
            </Typography>
          </Box>
        </Fade>

        {/* Methodology Steps */}
        <Grid container spacing={3}>
          {methodologySteps.map((step, index) => {
            const Icon = step.icon;
            return (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Fade in={true} timeout={1000} style={{ transitionDelay: `${index * 100}ms` }}>
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.1)'
                      },
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        bgcolor: step.color
                      }}
                    />
                    <CardContent sx={{ p: 4 }}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: `${step.color}15`,
                          mb: 2.5
                        }}
                      >
                        <Icon sx={{ fontSize: 32, color: step.color }} />
                      </Box>
                      <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: '1.25rem', md: '1.4rem' },
                          color: 'text.primary',
                          mb: 1.5
                        }}
                      >
                        {step.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.secondary',
                          fontSize: { xs: '0.95rem', md: '1rem' },
                          lineHeight: 1.7
                        }}
                      >
                        {step.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            );
          })}
        </Grid>

        {/* Additional Information */}
        <Box sx={{ mt: { xs: 6, md: 8 } }}>
          <Fade in={true} timeout={1000}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', md: '1.75rem' },
                  color: 'primary.main',
                  mb: 3
                }}
              >
                Our Commitment to Responsible Finance
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '0.95rem', md: '1rem' },
                  lineHeight: 1.7,
                  mb: 2
                }}
              >
                At Shoora Welfare Foundation, we believe in the power of responsible microfinance to create lasting positive change in communities. Our methodology is designed to ensure:
              </Typography>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                {[
                  "Transparent and fair lending practices",
                  "Sustainable business growth for borrowers",
                  "Strong community support networks",
                  "Financial literacy and business skills development",
                  "Regular monitoring and support",
                  "Progressive lending opportunities"
                ].map((point, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'background.default'
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: 'primary.main'
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.primary',
                          fontWeight: 500
                        }}
                      >
                        {point}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Fade>
        </Box>
      </Container>
    </Box>
  );
};

export default Methodology;